/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC, memo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Form,
  Modal as BootstrapModal,
  Spinner,
} from 'react-bootstrap';

import classNames from 'classnames';

import { Icon } from '@/components';
import { useToast } from '@/hooks';
import { tipCreationCoin } from '@/services';
import { loggedUserInfoStore } from '@/stores';
import { tryNormalLogged } from '@/utils/guard';

interface Props {
  className?: string;
  objectType: 'question' | 'answer';
  objectId: string;
  recipientUsername?: string;
}

const presets = [1, 5, 10, 20];

const CreationCoinTipButton: FC<Props> = ({
  className,
  objectType,
  objectId,
  recipientUsername,
}) => {
  const [visible, setVisible] = useState(false);
  const [amount, setAmount] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const { username = '' } = loggedUserInfoStore((state) => state.user);
  const toast = useToast();

  if (!objectId) {
    return null;
  }

  if (username && recipientUsername && username === recipientUsername) {
    return null;
  }

  const open = () => {
    if (!tryNormalLogged(true)) {
      return;
    }
    setVisible(true);
  };

  const close = () => {
    if (!submitting) {
      setVisible(false);
    }
  };

  const submit = () => {
    const nextAmount = Number(amount);
    if (!Number.isInteger(nextAmount) || nextAmount < 1 || nextAmount > 1000) {
      toast.onShow({
        msg: '打赏数量需为 1-1000 的整数',
        variant: 'danger',
      });
      return;
    }

    setSubmitting(true);
    tipCreationCoin({
      object_type: objectType,
      object_id: objectId,
      amount: nextAmount,
    })
      .then((resp) => {
        toast.onShow({
          msg: `已打赏 ${resp.amount} 创作币`,
          variant: 'success',
        });
        setVisible(false);
      })
      .catch((err) => {
        if (err?.msg) {
          toast.onShow({
            msg: err.msg,
            variant: 'danger',
          });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <Button
        className={classNames(className)}
        variant="outline-secondary"
        title="打赏作者"
        onClick={open}>
        <Icon type="bi" name="coin" className="me-2" />
        打赏
      </Button>

      <BootstrapModal show={visible} onHide={close} centered>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title as="h5">打赏作者</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <ButtonGroup className="w-100 mb-3">
            {presets.map((item) => (
              <Button
                key={item}
                variant={amount === item ? 'primary' : 'outline-secondary'}
                onClick={() => setAmount(item)}>
                {item}
              </Button>
            ))}
          </ButtonGroup>
          <Form.Control
            type="number"
            min={1}
            max={1000}
            step={1}
            value={amount}
            onChange={(evt) => setAmount(Number(evt.currentTarget.value))}
          />
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="link" disabled={submitting} onClick={close}>
            取消
          </Button>
          <Button disabled={submitting} onClick={submit}>
            {submitting && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            确认打赏
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>
    </>
  );
};

export default memo(CreationCoinTipButton);
